import { FC } from 'react';
import { VideoOff } from 'lucide-react';

import { Button } from '@new/common/ui';
import { detectBrowser, type BrowserName } from '../../lib/detectBrowser';
import type { ConnectionErrorKind } from '../../hooks/useTwilioRoom';

interface PermissionHelpPanelProps {
  kind: ConnectionErrorKind;
  onRetry?: () => void;
  browser?: BrowserName;
}

const PERMISSION_STEPS: Record<BrowserName, string[]> = {
  chrome: [
    'Click the camera icon (or the tune/lock icon) at the left end of the address bar.',
    'Set Camera and Microphone to "Allow".',
    'Click "Try again" below.',
  ],
  edge: [
    'Click the lock (or camera) icon at the left end of the address bar.',
    'Under "Permissions for this site", set Camera and Microphone to "Allow".',
    'Click "Try again" below.',
  ],
  firefox: [
    'Click the lock/permissions icon at the left end of the address bar.',
    'Remove the blocked Camera and Microphone entries (clear the "Blocked" status).',
    'Click "Try again" below and choose "Allow" when Firefox asks.',
  ],
  safari: [
    'Open Safari → Settings for This Website (or Safari menu → Settings → Websites).',
    'Set Camera and Microphone for this site to "Allow".',
    'Click "Try again" below.',
  ],
  other: [
    "Open your browser's site settings for this page.",
    'Set Camera and Microphone to "Allow".',
    'Click "Try again" below.',
  ],
};

const NON_PERMISSION_MESSAGE: Record<Exclude<ConnectionErrorKind, 'permission-denied'>, string> = {
  'missing-device':
    'No camera or microphone was found. Connect a camera and microphone (or enable your built-in devices), then try again.',
  'device-in-use':
    'Your camera or microphone could not be started. It may be in use by another application (for example Zoom, Teams, or another browser tab). Close it and try again.',
  unsupported:
    'Your browser could not access the camera and microphone. Try a different browser, or make sure the page is open over a secure (https) connection.',
  unknown: 'We could not connect you to the video call. Please try again.',
};

export const PermissionHelpPanel: FC<PermissionHelpPanelProps> = ({
  kind,
  onRetry,
  browser = detectBrowser(),
}) => {
  const isPermission = kind === 'permission-denied';
  const steps = isPermission ? PERMISSION_STEPS[browser] : undefined;

  return (
    <div
      role="alert"
      className="flex-1 min-w-0 h-full flex items-center justify-center bg-neutral-900/90 rounded-lg p-4"
    >
      <div className="max-w-sm w-full text-center flex flex-col items-center gap-3 bg-(--neutral-white-NW1000) rounded-xl shadow-lg p-6">
        <VideoOff className="w-10 h-10 text-(--neutral-black-NB500)" aria-hidden="true" />

        <h3 className="text-(--neutral-black-NB1000)! text-base font-semibold m-0">
          {isPermission ? 'Allow camera and microphone to join' : 'Could not start the video call'}
        </h3>

        {isPermission ? (
          <ol className="text-(--neutral-black-NB800) text-sm text-left m-0 pl-5 flex flex-col gap-1 list-decimal">
            {steps?.map((step, index) => (
              <li key={index}>{step}</li>
            ))}
          </ol>
        ) : (
          <p className="text-(--neutral-black-NB800) text-sm m-0">{NON_PERMISSION_MESSAGE[kind]}</p>
        )}

        {onRetry && (
          <Button variant="primary" size="sm" onClick={onRetry} className="mt-2">
            Try again
          </Button>
        )}
      </div>
    </div>
  );
};
